/**
 * Cycle Builder Wizard – main container
 * Company template: Method (+ pick template inline) → Review criteria → Events
 * System admin template: Method → Choose template → Review criteria → Events
 * Custom: Method → Details → Criteria → Events
 */
import React, { useState, useCallback, useRef, useMemo } from "react";
import { translateUi } from '../i18n/index';
import { useDeviceType } from "../useDeviceType";
import { CycleWizardProvider, useCycleWizard, getDurationInMonths, type WizardAssessmentEvent } from "./CycleWizardStore";
import { encodeCriteriaTypeForSave, flattenLibraryCycleToWizardItems, libraryCycleFromApiDetail, activeWizardCriteriaItems } from "./cycleLibraryUi";
import { CurrentCyclesPane, type CycleRow } from "./CurrentCyclesPane";
import { GlobalTemplatesPane } from "./GlobalTemplatesPane";
import { CycleWizardStep1, type ConfigStage } from "./CycleWizardStep1";
import { CycleWizardStep2 } from "./CycleWizardStep2";
import { CollapsiblePane } from "./CollapsiblePane";
import { WizardGuideBar, type GuideStepId } from "./WizardGuideBar";
import { cb, cbCn } from "./cycleBuilderTheme";
import { resolveDisplayedCycleMonths } from "./formatCycleDuration";

function newWizardClientKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `w-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function CycleWizardInner({
  currentUserEmail,
  companyCode,
  isSystemAdmin,
  isDarkMode = false,
  onSaved,
  onFrameworkApplied,
  onFrameworkRemoved,
  finishSetupFlow = false,
  showFrameworkPanes = true,
}: {
  currentUserEmail?: string | null;
  companyCode?: string;
  isSystemAdmin?: boolean;
  isDarkMode?: boolean;
  onSaved?: () => void;
  /** A template was applied as a company cycle — keep Finish company setup in sync. */
  onFrameworkApplied?: () => void;
  /** A company cycle was renamed, suspended or deleted. */
  onFrameworkRemoved?: () => void;
  /** Company still has setup steps outstanding — prompt for a first cycle. */
  finishSetupFlow?: boolean;
  /** Off when the host already provides template controls (Setup Wizard framework step). */
  showFrameworkPanes?: boolean;
}) {
  const { isMobileOrTablet: isMobile } = useDeviceType();
  const refreshPaneRef = useRef<() => void>(() => {});
  const refreshLibraryRef = useRef<() => void>(() => {});
  const refreshCompanyTemplatesRef = useRef<() => void>(() => {});
  const { state, dispatch } = useCycleWizard();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** After a successful save, holds title + assessment events so system admins can confirm the schedule (wizard state resets). */
  const [savedCycleSummary, setSavedCycleSummary] = useState<{
    title: string;
    events: WizardAssessmentEvent[];
  } | null>(null);
  const [manageOpen, setManageOpen] = useState(!isSystemAdmin);
  const [companyTemplatesPaneOpen, setCompanyTemplatesPaneOpen] = useState(true);
  const [manageCount, setManageCount] = useState<number | null>(null);
  const [builderOpen, setBuilderOpen] = useState(true);
  const [savedTemplateNotice, setSavedTemplateNotice] = useState<string | null>(null);
  const managePaneRef = useRef<HTMLDivElement | null>(null);
  const [configStage, setConfigStage] = useState<ConfigStage>("method");
  const [visitedStages, setVisitedStages] = useState<GuideStepId[]>(["method"]);
  const [wizardBuildMode, setWizardBuildMode] = useState<"template" | "scratch" | null>(null);
  const [editingCycleId, setEditingCycleId] = useState<number | null>(null);
  const [savingDraftCriteria, setSavingDraftCriteria] = useState(false);
  const builderRef = useRef<HTMLDivElement | null>(null);
  const companyWizardContext = Boolean(companyCode?.trim() && !isSystemAdmin);

  const toggleCompanyTemplatesPane = useCallback(() => {
    setCompanyTemplatesPaneOpen((open) => {
      const next = !open;
      if (next && manageCount === 0) setManageOpen(false);
      return next;
    });
  }, [manageCount]);

  const toggleManagePane = useCallback(() => {
    if (manageCount != null && manageCount > 0) return;
    setManageOpen((open) => {
      const next = !open;
      if (next) setCompanyTemplatesPaneOpen(false);
      return next;
    });
  }, [manageCount]);

  const goBackFromPickTemplate = useCallback(() => {
    setConfigStage("method");
    setWizardBuildMode(null);
  }, []);

  const markVisited = useCallback((id: GuideStepId) => {
    setVisitedStages((prev) => (prev.includes(id) ? prev : [...prev, id]));
  }, []);

  const handleConfigStageChange = useCallback(
    (stage: ConfigStage) => {
      setConfigStage(stage);
      markVisited(stage);
    },
    [markVisited]
  );

  const goToStep2 = useCallback(() => {
    markVisited("criteria");
    markVisited("events");
    dispatch({ type: "SET_STEP", step: 2 });
  }, [dispatch, markVisited]);

  const resetWizardProgress = useCallback(() => {
    setConfigStage("method");
    setVisitedStages(["method"]);
    setWizardBuildMode(null);
    setEditingCycleId(null);
  }, []);

  const goToStep1 = useCallback(() => {
    dispatch({ type: "SET_STEP", step: 1 });
    setConfigStage("criteria");
    markVisited("criteria");
  }, [dispatch, markVisited]);

  const handleGuideJump = useCallback(
    (id: GuideStepId) => {
      if (id === "events") {
        if (state.step === 2) return;
        return;
      }
      if (id === "details" && wizardBuildMode === "template" && configStage !== "details") {
        return;
      }
      if (id === "pickTemplate" && wizardBuildMode !== "template") {
        return;
      }
      if (state.step === 2) {
        dispatch({ type: "SET_STEP", step: 1 });
      }
      if (id === "method" || id === "pickTemplate" || id === "details" || id === "criteria") {
        setConfigStage(id);
        markVisited(id);
      }
    },
    [configStage, dispatch, markVisited, state.step, wizardBuildMode]
  );

  const handleEditDraftCriteria = useCallback(
    async (cycle: CycleRow) => {
      setSaveError(null);
      setBuilderOpen(true);
      setEditingCycleId(cycle.id);
      try {
        const headers: Record<string, string> = {};
        if (currentUserEmail) headers["X-User-Email"] = currentUserEmail;
        const [detailRes, eventsRes] = await Promise.all([
          fetch(`/api/cycles/${cycle.id}?v=${Date.now()}`, { cache: "no-store", headers }),
          fetch(`/api/cycles/${cycle.id}/events?v=${Date.now()}`, { cache: "no-store", headers }),
        ]);
        const detail = await detailRes.json().catch(() => ({}));
        if (!detailRes.ok) {
          setSaveError(typeof detail.error === "string" ? detail.error : translateUi("Could not load cycle for editing."));
          setEditingCycleId(null);
          return;
        }
        const preview = libraryCycleFromApiDetail(detail);
        if (!preview) {
          setSaveError(translateUi("This cycle has no criteria to edit yet."));
          setEditingCycleId(null);
          return;
        }
        const items = flattenLibraryCycleToWizardItems(preview).map((item) => {
          const elTitle = String(item.element ?? "").trim();
          const elNo = String(item.element_no ?? "").trim();
          return {
            ...item,
            clientKey: newWizardClientKey(),
            element: item.libraryKey && (!elTitle || elTitle === elNo) ? "" : item.element,
          };
        });
        const eventRows = eventsRes.ok ? await eventsRes.json().catch(() => []) : [];
        const loadedEvents = Array.isArray(eventRows) ? eventRows : [];
        const months = resolveDisplayedCycleMonths(cycle);
        dispatch({ type: "SET_CRITERIA", items });
        dispatch({ type: "SET_TITLE", title: cycle.title });
        if (months % 12 === 0) {
          dispatch({ type: "SET_DURATION", value: months / 12, unit: "years" });
        } else {
          dispatch({ type: "SET_DURATION", value: months, unit: "months" });
        }
        const fwId =
          typeof cycle.framework_cycle_id === "string" && cycle.framework_cycle_id.trim()
            ? cycle.framework_cycle_id.trim()
            : typeof detail.framework_cycle_id === "string"
              ? detail.framework_cycle_id.trim()
              : preview.id;
        dispatch({ type: "SET_SELECTED_FRAMEWORK_TEMPLATE", frameworkId: fwId || undefined });
        dispatch({ type: "SET_CYCLE_INFO_LOCKED", locked: true });
        if (loadedEvents.length > 0) {
          dispatch({
            type: "SET_EVENTS",
            events: loadedEvents.map((row: Record<string, unknown>, i: number) => ({
              event_name: String(row.event_name ?? ""),
              offset_months_open: Number(row.offset_months_open ?? 0),
              offset_months_close: Number(row.offset_months_close ?? 0),
              sort_order: Number(row.sort_order ?? i),
            })),
          });
        } else {
          dispatch({
            type: "SET_EVENTS",
            events: [
              {
                event_name: "",
                offset_months_open: 0,
                offset_months_close: Math.min(1, months),
                sort_order: 0,
              },
            ],
          });
        }
        dispatch({ type: "SET_STEP", step: 1 });
        setWizardBuildMode("template");
        setConfigStage("criteria");
        markVisited("method");
        markVisited("criteria");
        window.requestAnimationFrame(() => {
          builderRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      } catch (err: unknown) {
        setSaveError(err instanceof Error ? err.message : translateUi("Could not load cycle for editing."));
        setEditingCycleId(null);
      }
    },
    [currentUserEmail, dispatch, markVisited]
  );

  const buildWizardSaveBody = useCallback(
    (existingId: number | null) => {
      const trimmedTitle = state.title.trim();
      return {
        title: trimmedTitle,
        expiry_months: getDurationInMonths(state),
        expiry_years:
          state.durationUnit === "years" ? state.durationValue : Math.ceil(state.durationValue / 12),
        company_code: companyCode || null,
        framework_cycle_id: state.selectedFrameworkTemplateId || null,
        criteria: activeWizardCriteriaItems(state.criteriaItems).map((c) => ({
            unit_no: c.unit_no,
            element_no: c.element_no,
            element: c.element?.trim() || c.element_no?.trim() || "Element",
            criteria: encodeCriteriaTypeForSave(c.criteria, c.type),
            type: c.type,
            status: c.status || "Competent",
          })),
        events: state.assessmentEvents.map((e, i) => ({
          event_name: e.event_name.trim(),
          description: e.description?.trim() || undefined,
          offset_months_open: Number(e.offset_months_open),
          offset_months_close: Number(e.offset_months_close),
          sort_order: i,
          event_role: e.event_role || "standard",
        })),
        userEmail: currentUserEmail,
        ...(existingId ? { existing_cycle_id: String(existingId) } : {}),
      };
    },
    [companyCode, currentUserEmail, state]
  );

  /** Save criteria (and title/duration) on an existing draft without leaving the wizard. */
  const saveDraftCriteria = useCallback(async (): Promise<boolean> => {
    if (!editingCycleId) return false;
    setSaveError(null);
    const trimmedTitle = state.title.trim();
    if (!trimmedTitle) {
      setSaveError(translateUi("Enter a company cycle title before saving."));
      return false;
    }
    if (activeWizardCriteriaItems(state.criteriaItems).length === 0) {
      setSaveError(translateUi("Add at least one criterion before saving."));
      return false;
    }
    setSavingDraftCriteria(true);
    try {
      const res = await fetch("/api/cycles/wizard-save", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}),
        },
        body: JSON.stringify(buildWizardSaveBody(editingCycleId)),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(typeof data.error === "string" ? data.error : translateUi("Failed to save cycle"));
        return false;
      }
      refreshPaneRef.current?.();
      refreshCompanyTemplatesRef.current?.();
      return true;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : translateUi("Failed to save cycle");
      setSaveError(msg);
      return false;
    } finally {
      setSavingDraftCriteria(false);
    }
  }, [buildWizardSaveBody, currentUserEmail, editingCycleId]);

  const handleSave = useCallback(async () => {
    setSaveError(null);
    const trimmedTitle = state.title.trim();
    if (!trimmedTitle) {
      setSaveError("Enter a company cycle title before saving.");
      return;
    }
    setSaving(true);
    try {
      const body = buildWizardSaveBody(editingCycleId);

      const res = await fetch("/api/cycles/wizard-save", {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(currentUserEmail ? { "X-User-Email": currentUserEmail } : {}) },
        body: JSON.stringify(body),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSaveError(data.error || "Failed to save cycle");
        return;
      }
      if (editingCycleId) {
        dispatch({ type: "RESET" });
        resetWizardProgress();
        setSaveError(null);
        refreshPaneRef.current?.();
        refreshCompanyTemplatesRef.current?.();
        onFrameworkRemoved?.();
        setSavedTemplateNotice(translateUi('"{{title}}" updated.', { title: trimmedTitle }));
        return;
      }
      setSavedCycleSummary({
        title: trimmedTitle,
        events: state.assessmentEvents.map((e) => ({ ...e })),
      });
      dispatch({ type: "RESET" });
      resetWizardProgress();
      setSaveError(null);
      refreshPaneRef.current?.();
      refreshLibraryRef.current?.();
      refreshCompanyTemplatesRef.current?.();
      onSaved?.();
    } catch (err: unknown) {
      const msg = err && typeof err === "object" && "message" in err ? String((err as { message?: string }).message) : "";
      setSaveError(msg || "Failed to save cycle");
    } finally {
      setSaving(false);
    }
  }, [buildWizardSaveBody, currentUserEmail, dispatch, editingCycleId, onFrameworkRemoved, onSaved, resetWizardProgress]);

  const onManageCountChange = useCallback((count: number) => {
    setManageCount(count);
    if (count > 0) setManageOpen(true);
  }, []);

  const handleTemplateApplied = useCallback(() => {
    refreshCompanyTemplatesRef.current?.();
    refreshPaneRef.current?.();
    setManageOpen(true);
    onFrameworkApplied?.();
  }, [onFrameworkApplied]);

  /** A cycle was deleted, suspended or reactivated — the assignable list changed. */
  const handleCyclesChanged = useCallback(() => {
    refreshCompanyTemplatesRef.current?.();
    onFrameworkRemoved?.();
  }, [onFrameworkRemoved]);

  const bindCompanyTemplatesRefresh = useCallback((fn: () => void) => {
    refreshCompanyTemplatesRef.current = fn;
  }, []);

  const closeBuilderAndShowManage = useCallback(
    (notice?: string | null) => {
      dispatch({ type: "RESET" });
      resetWizardProgress();
      setSaveError(null);
      setBuilderOpen(false);
      setManageOpen(true);
      setSavedTemplateNotice(notice ?? null);
      refreshPaneRef.current?.();
      refreshLibraryRef.current?.();
      window.requestAnimationFrame(() => {
        managePaneRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    },
    [dispatch, resetWizardProgress]
  );

  const reopenBuilder = useCallback(() => {
    dispatch({ type: "RESET" });
    resetWizardProgress();
    setSavedTemplateNotice(null);
    setBuilderOpen(true);
  }, [dispatch, resetWizardProgress]);

  const activeGuide: GuideStepId = state.step === 2 ? "events" : configStage;

  const completedIds = useMemo(() => {
    const ids = new Set<GuideStepId>(visitedStages);
    if (state.step === 2) {
      ids.add("method");
      ids.add("pickTemplate");
      ids.add("details");
      ids.add("criteria");
    }
    return Array.from(ids);
  }, [visitedStages, state.step]);

  const hideDetailsStep = wizardBuildMode === "template" && configStage !== "details";
  const hidePickTemplateStep = wizardBuildMode !== "template";
  const stageBlurb =
    wizardBuildMode === "template" && configStage !== "details"
      ? activeGuide === "method"
        ? companyWizardContext
          ? translateUi("Step 1 — choose a template or build custom.")
          : translateUi("Step 1 of 4 — start from a saved template.")
        : activeGuide === "pickTemplate"
          ? companyWizardContext
            ? translateUi("Step 2 — pick a template from those available to your company.")
            : translateUi("Step 2 of 4 — choose a template. Name and duration come with it.")
          : activeGuide === "criteria"
            ? translateUi("Step 3 of 4 — review the units and criteria in this template.")
            : translateUi("Step 4 of 4 — schedule assessment events, then save.")
      : activeGuide === "method"
        ? translateUi("Step 1 — choose a template or build custom.")
        : activeGuide === "pickTemplate"
          ? companyWizardContext
            ? translateUi("Step 2 — pick a template from those available to your company.")
            : translateUi("Step 2 of 4 — choose a template. Name and duration come with it.")
          : activeGuide === "details"
            ? wizardBuildMode === "template"
              ? translateUi("Enter a cycle title and duration, then continue.")
              : translateUi("Step 2 of 4 — name the cycle and set duration.")
            : activeGuide === "criteria"
              ? translateUi("Step 3 of 4 — add the competency criteria.")
              : translateUi("Step 4 of 4 — schedule assessment events, then save.");

  if (savedCycleSummary) {
    const { title: savedTitle, events: savedEvents } = savedCycleSummary;
    return (
      <div data-cycle-builder className="space-y-6">
        <div
          className={cbCn(
            "rounded-xl border p-6 text-center",
            isDarkMode ? "border-emerald-500/40 bg-emerald-950/40" : "border-emerald-200 bg-emerald-50"
          )}
        >
          <h3 className={cbCn("text-lg font-semibold", isDarkMode ? "text-emerald-200" : "text-emerald-800")}>
            {translateUi("Cycle created successfully")}
          </h3>
          <p className={cbCn("mt-2", cb.body(isDarkMode))}>
            &quot;{savedTitle}&quot; has been saved with this title. Managers can now assign it to employees. It is listed under
            &quot;Your cycles&quot; on this page, where you can rename or suspend it.
          </p>
          {isSystemAdmin && savedEvents.length > 0 && (
            <div
              className={cbCn(
                "mt-6 w-full max-w-none text-left rounded-lg border px-4 py-4 sm:px-5",
                isDarkMode ? "border-slate-600 bg-slate-900/50" : "border-emerald-200/80 bg-white/80"
              )}
            >
              <h4 className={cbCn("text-sm font-semibold uppercase tracking-wide", isDarkMode ? "text-emerald-200" : "text-emerald-900")}>
                {translateUi("Assessment events on this cycle")}
              </h4>
              <p className={cbCn("mt-1 text-xs sm:text-sm", cb.body(isDarkMode))}>
                {translateUi("Relative month offsets from the assignment start date when this cycle is assigned to an employee.")}
              </p>
              <ol className={cbCn("mt-3 list-decimal list-inside space-y-2 text-sm sm:text-base", cb.body(isDarkMode))}>
                {savedEvents.map((ev, i) => (
                  <li key={i} className="break-words pl-0.5">
                    <span className={cbCn("font-medium", isDarkMode ? "text-slate-100" : "text-slate-900")}>
                      {ev.event_name.trim() || "—"}
                    </span>
                    <span className={isDarkMode ? "text-slate-400" : "text-slate-600"}>
                      {" "}
                      — open month {ev.offset_months_open}, close month {ev.offset_months_close}
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className={cbCn("mt-4 flex gap-2", isMobile ? "flex-col" : "items-center justify-center")}>
            <button
              type="button"
              onClick={() => setSavedCycleSummary(null)}
              className={cbCn(
                "rounded-xl border px-6 py-2.5 font-medium",
                isMobile ? "w-full" : "",
                isDarkMode
                  ? "border-indigo-500/50 bg-slate-800 text-indigo-200 hover:bg-slate-700"
                  : "border-indigo-200 bg-white text-indigo-700 hover:bg-indigo-50"
              )}
            >
              {translateUi("Back to Cycle Builder")}
            </button>
            <button
              type="button"
              onClick={() => setSavedCycleSummary(null)}
              className={cbCn("rounded-xl px-6 py-2.5 font-medium text-white bg-indigo-600 hover:bg-indigo-700", isMobile ? "w-full" : "")}
            >
              {translateUi("Create another cycle")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const manageSummary =
    manageCount == null ? "Loading…" : manageCount === 0 ? "None yet" : `${manageCount} ${manageCount === 1 ? "item" : "items"}`;

  return (
    <div data-cycle-builder className={cbCn("space-y-5", isMobile ? "px-2 -mx-2" : "")}>
      <div>
        <h2 className={cb.headingLg(isDarkMode)}>{translateUi("Cycle Builder")}</h2>
        <p className={cbCn("mt-1 text-sm", cb.bodySm(isDarkMode))}>
          {builderOpen
            ? stageBlurb
            : translateUi("Your template is in the list below. Open it to edit, or create another cycle.")}
        </p>
      </div>

      {builderOpen && (
        <WizardGuideBar
          active={activeGuide}
          completedIds={completedIds}
          onJump={handleGuideJump}
          isDarkMode={isDarkMode}
          isMobile={isMobile}
          hiddenIds={[
            ...(hideDetailsStep ? (["details"] as GuideStepId[]) : []),
            ...(hidePickTemplateStep ? (["pickTemplate"] as GuideStepId[]) : []),
          ]}
        />
      )}

      {saveError && (
        <div className={cbCn("rounded-lg border px-4 py-3 text-sm", cb.alertError(isDarkMode))}>{saveError}</div>
      )}

      {savedTemplateNotice && (
        <div className={cbCn("rounded-lg border px-4 py-3 text-sm", cb.alertSuccess(isDarkMode))} role="status">
          {savedTemplateNotice}
        </div>
      )}

      {!isSystemAdmin && showFrameworkPanes && finishSetupFlow && manageCount === 0 && (
        <div
          className={cbCn(
            "rounded-lg border px-4 py-3 text-sm",
            isDarkMode ? "border-amber-500/40 bg-amber-950/40 text-amber-100" : "border-amber-200 bg-amber-50 text-amber-900"
          )}
          role="note"
        >
          {translateUi(
            "Finish setup: choose a template or build a custom cycle in the builder. Saved company cycles stay in draft until you release them to managers."
          )}
        </div>
      )}

      <div ref={builderRef}>
      {builderOpen ? (
        state.step === 1 ? (
          <CycleWizardStep1
            configStage={configStage}
            onConfigStageChange={handleConfigStageChange}
            onBuildModeChange={setWizardBuildMode}
            buildModeHint={wizardBuildMode}
            onNext={goToStep2}
            onMounted={(fn) => {
              refreshLibraryRef.current = fn;
            }}
            onCompanyTemplatesMounted={bindCompanyTemplatesRefresh}
            onTemplateApplied={handleTemplateApplied}
            onGlobalTemplateSaved={(title) => {
              closeBuilderAndShowManage(
                title
                  ? translateUi('Saved "{{title}}" to Global Templates.', { title })
                  : translateUi("Saved to Global Templates.")
              );
            }}
            currentUserEmail={currentUserEmail}
            companyCode={companyCode}
            isSystemAdmin={isSystemAdmin}
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            editingDraftCycleId={editingCycleId}
            onSaveDraftCriteria={editingCycleId != null ? saveDraftCriteria : undefined}
            savingDraftCriteria={savingDraftCriteria}
            companyTemplatesPaneOpen={companyTemplatesPaneOpen}
            onCompanyTemplatesPaneToggle={toggleCompanyTemplatesPane}
            suppressBackButton={companyWizardContext && configStage === "pickTemplate"}
          />
        ) : (
          <CycleWizardStep2
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            onBack={goToStep1}
            onSave={handleSave}
            saving={saving}
            editingExistingCycle={editingCycleId != null}
            currentUserEmail={currentUserEmail}
            companyCode={companyCode}
          />
        )
      ) : (
        <button
          type="button"
          onClick={reopenBuilder}
          className={cbCn(
            "rounded-xl px-6 py-2.5 font-medium text-white bg-indigo-600 hover:bg-indigo-700",
            isMobile ? "w-full min-h-[48px]" : ""
          )}
        >
          {translateUi("Create another cycle")}
        </button>
      )}
      </div>

      <div ref={managePaneRef}>
      <CollapsiblePane
        open={manageCount != null && manageCount > 0 ? true : manageOpen}
        onToggle={toggleManagePane}
        disabled={manageCount != null && manageCount > 0}
        title={
          isSystemAdmin
            ? translateUi("Manage global templates")
            : translateUi("Your cycles — what managers can assign")
        }
        summary={manageSummary}
        isDarkMode={isDarkMode}
        isMobile={isMobile}
        bareBody
        pad="sm"
      >
        {isSystemAdmin ? (
          <GlobalTemplatesPane
            embedded
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            currentUserEmail={currentUserEmail}
            onCountChange={onManageCountChange}
            onMounted={(fn) => {
              refreshPaneRef.current = fn;
            }}
            onTemplatesChanged={() => {
              refreshPaneRef.current?.();
              refreshLibraryRef.current?.();
            }}
          />
        ) : (
          <CurrentCyclesPane
            embedded
            isMobile={isMobile}
            isDarkMode={isDarkMode}
            companyCode={companyCode}
            currentUserEmail={currentUserEmail}
            isSystemAdmin={isSystemAdmin}
            onCountChange={onManageCountChange}
            onChanged={handleCyclesChanged}
            onEditCriteriaInBuilder={companyWizardContext ? handleEditDraftCriteria : undefined}
            onMounted={(fn) => {
              refreshPaneRef.current = fn;
            }}
          />
        )}
      </CollapsiblePane>
      </div>

      {builderOpen && state.step === 1 && companyWizardContext && configStage === "pickTemplate" && (
        <button
          type="button"
          onClick={goBackFromPickTemplate}
          className={cbCn(
            "rounded-xl font-medium",
            isMobile ? "w-full py-3 min-h-[48px] text-base" : "px-6 py-2.5",
            cb.btnSecondary(isDarkMode)
          )}
        >
          {translateUi("Back")}
        </button>
      )}
    </div>
  );
}

export function CycleWizardPage({
  currentUserEmail,
  companyCode,
  isSystemAdmin,
  isDarkMode = false,
  onSaved,
  onFrameworkApplied,
  onFrameworkRemoved,
  finishSetupFlow = false,
  showFrameworkPanes = true,
}: {
  currentUserEmail?: string | null;
  companyCode?: string;
  isSystemAdmin?: boolean;
  isDarkMode?: boolean;
  onSaved?: () => void;
  onFrameworkApplied?: () => void;
  onFrameworkRemoved?: () => void;
  finishSetupFlow?: boolean;
  showFrameworkPanes?: boolean;
}) {
  return (
    <CycleWizardProvider>
      <CycleWizardInner
        currentUserEmail={currentUserEmail}
        companyCode={companyCode}
        isSystemAdmin={isSystemAdmin}
        isDarkMode={isDarkMode}
        onSaved={onSaved}
        onFrameworkApplied={onFrameworkApplied}
        onFrameworkRemoved={onFrameworkRemoved}
        finishSetupFlow={finishSetupFlow}
        showFrameworkPanes={showFrameworkPanes}
      />
    </CycleWizardProvider>
  );
}
